import { useQuery, type QueryClient } from '@tanstack/react-query';

import {
  DESKTOP_TARGETS_QUERY_KEY,
  desktopTargetsClient,
  type DesktopTargetListResponse,
} from './desktopTargets';

const ROSTER_STALE_TIME_MS = 30_000;
const ROSTER_MAX_BACKOFF_MS = 30_000;

const rosterBackoffMs = (failureCount: number): number =>
  Math.min(1000 * 2 ** failureCount, ROSTER_MAX_BACKOFF_MS);

export const readDesktopTargetRosterState = (
  queryClient: QueryClient
): { rosterReady: boolean; roster: DesktopTargetListResponse | undefined } => {
  const state = queryClient.getQueryState(DESKTOP_TARGETS_QUERY_KEY);
  const roster = queryClient.getQueryData<DesktopTargetListResponse>(
    DESKTOP_TARGETS_QUERY_KEY
  );
  return {
    // Stale-while-error: cached data from an earlier success stays usable even
    // if the latest refetch errored, so a transient fetch failure at send time
    // doesn't silently strip the user's selected computer from the turn.
    rosterReady: state?.status === 'success' || roster !== undefined,
    roster,
  };
};

export const useDesktopTargetRoster = (enabled: boolean) => {
  return useQuery({
    queryKey: DESKTOP_TARGETS_QUERY_KEY,
    queryFn: async () => (await desktopTargetsClient.list()).data,
    enabled,
    staleTime: ROSTER_STALE_TIME_MS,
    // Let failures settle so the selector's Retry action can refetch
    // immediately. The interval below retains automatic backoff retries.
    retry: false,
    refetchInterval: (query) =>
      query.state.status === 'error'
        ? rosterBackoffMs(query.state.fetchFailureCount)
        : ROSTER_STALE_TIME_MS,
  });
};
